import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

// Resets scroll on route change. If a hash is present, scrolls to the element.
export default function ScrollToTop() {
  const location = useLocation();
  const action = useNavigationType();
  const isInitialMount = useRef(true);

  // Disable browser scroll restoration for SPA routing
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      try { window.history.scrollRestoration = 'manual'; } catch { /* noop */ }
    }
  }, []);

  const scrollTopNow = () => {
    // Reset both possible scroll containers for robustness
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  };

  // Use layout effect to run earlier in the paint cycle on navigation
  useLayoutEffect(() => {
    // JAMAIS au montage initial. Les pages sont prérendues : l'utilisateur lit et
    // fait défiler pendant que le JS (~400 Ko gzip sur /gradient-descent) se
    // charge encore. Cet effet tournait à l'hydratation — 1 à 3 s après le premier
    // rendu sur un réseau réel — et le renvoyait brutalement en haut de page :
    // à l'écran, « la page se recharge ». Invisible en local (JS instantané) et
    // avant le prerender (page vide jusqu'au JS, donc scroll déjà à zéro).
    // Le navigateur place déjà une navigation fraîche en haut ; ce composant ne
    // doit agir que sur les changements de route côté client.
    // Trace de diagnostic : permet de vérifier depuis la console quel chemin cet
    // effet a pris à chaque exécution ('skip-initial' ou 'reset'), y compris dans
    // un onglet en arrière-plan où requestAnimationFrame ne tire jamais.
    interface Traced { __scrollFx?: string[] }
    const fx = ((window as unknown as Traced).__scrollFx ??= []);

    if (isInitialMount.current) {
      isInitialMount.current = false;
      fx.push('skip-initial:' + location.pathname);
      return;
    }
    fx.push('reset:' + location.pathname);

    const { hash } = location;

    // If there's a hash, try to scroll to the anchor instead of top
    if (hash) {
      const id = hash.slice(1);
      // Two-frame delay to let new route mount and measure
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'auto', block: 'start' });
        } else {
          scrollTopNow();
        }
      });
      return;
    }

    // Always reset on PUSH/REPLACE/POP to keep UX consistent
    // Delay one frame to avoid the browser restoring scroll after POP
    requestAnimationFrame(() => {
      scrollTopNow();
      // Fallback in case late content changes push the page down
      setTimeout(scrollTopNow, 0);
    });
  }, [location.pathname, location.search, location.hash, action]);

  return null;
}
